import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { useToast } from '@/components/ui/use-toast'
import { useAuth } from '@/contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import ReCAPTCHA from 'react-google-recaptcha'
import * as gtag from "@/utils/gtag";
import { pushEvent, GTMEvents } from "@/utils/gtm";

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

// Debug log
console.log('reCAPTCHA Site Key:', RECAPTCHA_SITE_KEY);

const formSchema = z.object({
  fullName: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
  acceptTerms: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the Terms of Service and Privacy Policy.' })
  })
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type FormValues = z.infer<typeof formSchema>

export function RegisterForm() {
  const [isLoading, setIsLoading] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const { toast } = useToast()
  const { register: registerUser } = useAuth()
  const navigate = useNavigate()

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false,
    },
  })

  const onSubmit = async (values: FormValues) => {
    try {
      // Track form start
      pushEvent({
        event: GTMEvents.FORM_START,
        category: 'User',
        action: 'Registration',
        label: 'Start Registration Form'
      });

      if (!captchaToken) {
        toast({
          variant: 'destructive',
          title: 'CAPTCHA Required',
          description: 'Please complete the CAPTCHA verification.',
        });
        return;
      }

      setIsLoading(true)
      
      await registerUser(values.email, values.password)

      // Track successful registration
      gtag.event({
        action: 'sign_up',
        category: 'engagement',
        label: 'User Registration'
      });

      // Track successful registration in GTM
      pushEvent({
        event: GTMEvents.SIGN_UP,
        category: 'User',
        action: 'Registration',
        label: 'Successful Registration',
        user_email: values.email,
        user_name: values.fullName
      });

      toast({
        title: 'Registration successful!',
        description: 'Please check your email to verify your account.',
      })
      
      form.reset()
      setCaptchaToken(null)
      if (window.grecaptcha) {
        window.grecaptcha.reset()
      }
      
      // Redirect to homepage after successful registration
      setTimeout(() => {
        navigate('/')
      }, 2000) // Wait 2 seconds to show the success message
    } catch (error) {
      console.error('Registration error:', error)
      
      // Track registration error in GA4
      gtag.event({
        action: 'sign_up_error',
        category: 'error',
        label: error instanceof Error ? error.message : 'Unknown error'
      });

      // Track registration error in GTM
      pushEvent({
        event: GTMEvents.FORM_ERROR,
        category: 'User',
        action: 'Registration',
        label: error instanceof Error ? error.message : 'Unknown error',
        error_type: 'Registration Error'
      });

      toast({
        variant: 'destructive',
        title: 'Registration failed',
        description: error instanceof Error 
          ? error.message 
          : 'Failed to create account. Please try again later.',
      })
      // Reset CAPTCHA on failure
      setCaptchaToken(null)
      if (window.grecaptcha) {
        window.grecaptcha.reset()
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Enter your full name" 
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input 
                  type="email"
                  placeholder="Enter your email" 
                  autoComplete="email"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <Input 
                  type="password" 
                  placeholder="Enter your password" 
                  autoComplete="new-password"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirm Password</FormLabel>
              <FormControl>
                <Input 
                  type="password" 
                  placeholder="Confirm your password" 
                  autoComplete="new-password"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Terms and Privacy Policy Checkbox */}
        <FormField
          control={form.control}
          name="acceptTerms"
          render={({ field }) => (
            <FormItem>
              <div
                className="flex items-center gap-3 p-2 rounded-md transition-colors hover:bg-sigma-grey/40"
              >
                <FormControl>
                  <Input
                    type="checkbox"
                    checked={field.value}
                    onChange={field.onChange}
                    className="w-5 h-5 accent-primary border-sigma-grey focus:ring-2 focus:ring-primary focus:ring-offset-1 transition-all duration-150"
                  />
                </FormControl>
                <FormLabel className="mb-0 font-hind text-sm text-sigma-black font-normal select-none cursor-pointer">
                  I accept the{' '}
                  <a href="/TermsOfService" target="_blank" rel="noopener noreferrer" className="text-sigma-red hover:underline font-medium">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/PrivacyPolicy" target="_blank" rel="noopener noreferrer" className="text-sigma-red hover:underline font-medium">Privacy Policy</a>
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-center my-4">
          <ReCAPTCHA
            sitekey={RECAPTCHA_SITE_KEY || ''}
            onChange={handleCaptchaChange}
            theme="light"
          />
        </div>

        <Button 
          type="submit" 
          className="w-full" 
          disabled={isLoading || !captchaToken}
        >
          {isLoading ? 'Creating account...' : 'Create account'}
        </Button>
      </form>
    </Form>
  )
} 